/**
 * BeezFy Size Chart System - Vanilla JavaScript
 * Lightweight, accessible drawer implementation with focus management
 * No external dependencies
 */

(function() {
  'use strict';

  class BeezfySizeChart {
    constructor() {
      this.drawer = null;
      this.backdrop = null;
      this.triggers = [];
      this.closeButtons = [];
      this.unitButtons = [];
      this.lastFocusedElement = null;
      this.isOpen = false;

      this.init();
    }

    init() {
      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      this.drawer = document.getElementById('bfy-sizechart-drawer');

      // Fail-safe: Exit if drawer not found
      if (!this.drawer) {
        return;
      }

      this.backdrop = this.drawer.querySelector('[data-sizechart-backdrop]');
      this.triggers = Array.from(document.querySelectorAll('[data-sizechart-open]'));
      this.closeButtons = Array.from(this.drawer.querySelectorAll('[data-sizechart-close]'));
      this.unitButtons = Array.from(this.drawer.querySelectorAll('[data-unit]'));

      this.bindEvents();
      this.setupDeepLink();
    }

    bindEvents() {
      // Trigger buttons
      this.triggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Close buttons
      this.closeButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });

      // Backdrop click
      if (this.backdrop) {
        this.backdrop.addEventListener('click', () => {
          this.close();
        });
      }

      // ESC key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
        }
      });

      // Unit toggle buttons
      this.unitButtons.forEach(button => {
        button.addEventListener('click', () => {
          this.toggleUnit(button);
        });
      });

      // Prevent drawer content clicks from closing drawer
      const content = this.drawer.querySelector('.bfy-sizechart-content');
      if (content) {
        content.addEventListener('click', (e) => {
          e.stopPropagation();
        });
      }
    }

    setupDeepLink() {
      // Support for #sizechart deep link
      if (window.location.hash === '#sizechart') {
        // Small delay to ensure page is fully loaded
        setTimeout(() => {
          this.open();
        }, 100);
      }

      // Listen for hash changes
      window.addEventListener('hashchange', () => {
        if (window.location.hash === '#sizechart' && !this.isOpen) {
          this.open();
        }
      });

      // Tasto "indietro" del browser mentre il drawer e' aperto: chiude senza toccare la history
      // (fix dal fork Melydonna: prima close() faceva pushState e la history si riempiva di voci fantasma:
      // chiudi -> "indietro" riapriva il drawer -> "indietro" di nuovo per uscire dalla pagina)
      window.addEventListener('popstate', () => {
        if (window.location.hash !== '#sizechart' && this.isOpen) {
          this._closingFromHistory = true;
          this.close();
          this._closingFromHistory = false;
        }
      });
    }

    open() {
      if (!this.drawer || this.isOpen) return;

      // Store the currently focused element
      this.lastFocusedElement = document.activeElement;

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Show drawer
      this.drawer.classList.add('active');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.isOpen = true;

      // Focus management
      setTimeout(() => {
        const firstFocusable = this.getFirstFocusableElement();
        if (firstFocusable) {
          firstFocusable.focus();
        }
      }, 100);

      // Trap focus within drawer
      this.trapFocus();

      // Update URL hash (optional, for deep linking): stato marcato, cosi' close() sa se e' suo
      if (window.location.hash !== '#sizechart') {
        history.pushState({ beezfySizechart: true }, '', '#sizechart');
      }
    }

    close() {
      if (!this.drawer || !this.isOpen) return;

      // Restore body scroll
      document.body.style.overflow = '';

      // Hide drawer
      this.drawer.classList.remove('active');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.isOpen = false;

      // Return focus to trigger element
      if (this.lastFocusedElement) {
        this.lastFocusedElement.focus();
        this.lastFocusedElement = null;
      }

      // Remove focus trap
      this.removeFocusTrap();

      // Remove hash: se la voce l'ha messa open(), si torna indietro (la voce sparisce);
      // se veniva da un deep link o dal tasto indietro, si sostituisce senza aggiungere nulla
      if (window.location.hash === '#sizechart' && !this._closingFromHistory) {
        if (history.state && history.state.beezfySizechart) {
          history.back();
        } else {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      }
    }

    toggleUnit(button) {
      if (!button) return;

      const unit = button.getAttribute('data-unit');
      if (!unit) return;

      // Update button states
      this.unitButtons.forEach(btn => {
        const isActive = btn === button;
        btn.classList.toggle('active', isActive);
        btn.setAttribute('aria-pressed', isActive.toString());
      });

      // Here you could implement actual unit conversion
      // For now, just trigger a custom event that other scripts can listen to
      this.drawer.dispatchEvent(new CustomEvent('sizechart:unitChanged', {
        detail: { unit, button }
      }));
    }

    getFirstFocusableElement() {
      const focusableElements = this.drawer.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      return focusableElements[0];
    }

    getLastFocusableElement() {
      const focusableElements = this.drawer.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      return focusableElements[focusableElements.length - 1];
    }

    trapFocus() {
      this.focusTrapHandler = (e) => {
        if (e.key !== 'Tab') return;

        const firstFocusable = this.getFirstFocusableElement();
        const lastFocusable = this.getLastFocusableElement();

        if (e.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable?.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable?.focus();
          }
        }
      };

      document.addEventListener('keydown', this.focusTrapHandler);
    }

    removeFocusTrap() {
      if (this.focusTrapHandler) {
        document.removeEventListener('keydown', this.focusTrapHandler);
        this.focusTrapHandler = null;
      }
    }

    // Public API for external access
    destroy() {
      this.close();
      // Remove all event listeners
      // This could be expanded if needed for cleanup
    }
  }

  // Auto-initialize when script loads
  const sizeChart = new BeezfySizeChart();

  // Production ready - Size Chart system initialized
  window.BeezfySizeChart = sizeChart;

  // Support for module environments (if needed)
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = BeezfySizeChart;
  }

})();